import { nanoid } from '@reduxjs/toolkit';
import type { Widget, Position, Size } from '../types/index.js';
import { useCanvasStore } from '../stores/canvasStore.js';

// Simple event emitter interface for browser compatibility
interface EventEmitter {
  on(event: string, listener: (...args: any[]) => void): void;
  off(event: string, listener: (...args: any[]) => void): void;
  emit(event: string, ...args: any[]): void;
  removeAllListeners(): void;
}

// Basic event emitter implementation
class SimpleEventEmitter implements EventEmitter {
  private events: Map<string, Set<(...args: any[]) => void>> = new Map();

  on(event: string, listener: (...args: any[]) => void): void {
    if (!this.events.has(event)) {
      this.events.set(event, new Set());
    }
    this.events.get(event)!.add(listener);
  }

  off(event: string, listener: (...args: any[]) => void): void {
    this.events.get(event)?.delete(listener);
  }

  emit(event: string, ...args: any[]): void {
    this.events.get(event)?.forEach(listener => {
      try {
        listener(...args);
      } catch (error) {
        console.error('Event listener error:', error);
      }
    });
  }

  removeAllListeners(): void {
    this.events.clear();
  }
}

// Simple logger for browser environment
const logger = {
  info: (...args: any[]) => console.log('[AgentFeedback]', ...args),
  debug: (...args: any[]) => console.debug('[AgentFeedback]', ...args),
  warn: (...args: any[]) => console.warn('[AgentFeedback]', ...args),
  error: (...args: any[]) => console.error('[AgentFeedback]', ...args)
};

/**
 * Action states for tracking agent tool execution
 */
export type AgentActionStatus = 'pending' | 'executing' | 'completed' | 'failed' | 'cancelled';

/**
 * Types of agent actions that can be performed
 */
export type AgentActionType = 'createWidget' | 'connectWidgets' | 'updateWidget' | 
  'deleteWidget' | 'executeDataFlow' | 'findWidget' | 'getWidgetData';

/**
 * Canvas preview data for visual overlays
 */
export interface CanvasPreview {
  id: string;
  type: 'widget' | 'connection' | 'highlight';
  position?: Position;
  size?: Size;
  targetWidgetId?: string;
  sourceWidgetId?: string;
  style?: {
    opacity?: number;
    color?: string;
    animation?: 'pulse' | 'glow' | 'fade' | 'slide';
    duration?: number;
  };
}

/**
 * Agent action with execution state and preview data
 */
export interface AgentAction {
  id: string;
  type: AgentActionType;
  status: AgentActionStatus;
  timestamp: number;
  duration?: number;
  
  // Tool call details
  toolCallId?: string;
  toolName: string;
  args: Record<string, any>;
  result?: any;
  
  // Target information
  targetWidgetId?: string;
  sourceWidgetId?: string;
  connectionId?: string;
  
  // Visual feedback
  preview?: CanvasPreview;
  description: string;
  
  // Error handling
  error?: string;
  retryCount?: number;
  
  // Metadata
  metadata?: {
    user?: string;
    session?: string;
    canvasId?: string;
    [key: string]: any;
  };
}

/**
 * Status update payload for real-time feedback
 */
export interface ActionStatusUpdate {
  actionId: string;
  status: AgentActionStatus;
  progress?: number;
  message?: string;
  timestamp: number;
  duration?: number;
  error?: string;
  result?: any;
}

/**
 * Visual feedback configuration
 */
export interface VisualFeedbackConfig {
  showPreviews: boolean;
  animationDuration: number;
  highlightColor: string;
  pulseIntensity: number;
  enableParticles: boolean;
  maxConcurrentActions: number;
}

/**
 * AgentFeedbackService - Real-time visual feedback for agent actions
 * 
 * Tracks agent action execution states, provides real-time status updates,
 * and generates canvas preview data for visual overlays.
 */
export class AgentFeedbackService extends SimpleEventEmitter {
  private actions = new Map<string, AgentAction>();
  private activeActions = new Set<string>();
  private completedActions: string[] = [];
  private maxHistorySize = 100;
  private statusUpdateInterval = 50; // 50ms for < 100ms response time
  private cleanupTimer?: number;
  
  private config: VisualFeedbackConfig = {
    showPreviews: true,
    animationDuration: 300,
    highlightColor: '#4CAF50',
    pulseIntensity: 0.6,
    enableParticles: false,
    maxConcurrentActions: 5
  };

  constructor() {
    super();
    this.startCleanupTimer();
    
    // Log service initialization
    logger.info('AgentFeedbackService initialized', {
      service: 'AgentFeedbackService',
      maxHistorySize: this.maxHistorySize,
      statusUpdateInterval: this.statusUpdateInterval
    });
  }

  /**
   * Track a new agent action
   */
  trackAction(
    toolCallId: string,
    toolName: string,
    args: Record<string, any>,
    options: {
      description?: string;
      targetWidgetId?: string;
      sourceWidgetId?: string;
      preview?: Partial<CanvasPreview>;
      metadata?: Record<string, any>;
    } = {}
  ): string {
    const actionId = nanoid();
    const timestamp = Date.now();
    
    // Determine action type from tool name
    const actionType = this.mapToolNameToActionType(toolName);
    
    // Generate description if not provided
    const description = options.description || this.generateActionDescription(toolName, args);
    
    // Create preview data if enabled
    let preview: CanvasPreview | undefined;
    if (this.config.showPreviews && options.preview) {
      preview = {
        id: nanoid(),
        type: actionType === 'connectWidgets' ? 'connection' : 'widget',
        ...options.preview,
        style: {
          opacity: 0.6,
          color: this.config.highlightColor,
          animation: 'pulse',
          duration: this.config.animationDuration,
          ...options.preview.style
        }
      };
    }

    const action: AgentAction = {
      id: actionId,
      type: actionType,
      status: 'pending',
      timestamp,
      toolCallId,
      toolName,
      args,
      description,
      targetWidgetId: options.targetWidgetId,
      sourceWidgetId: options.sourceWidgetId,
      preview,
      metadata: {
        canvasId: useCanvasStore.getState().activeCanvasId,
        ...options.metadata
      }
    };

    // Store action and mark as active
    this.actions.set(actionId, action);
    this.activeActions.add(actionId);
    
    // Enforce max concurrent actions
    if (this.activeActions.size > this.config.maxConcurrentActions) {
      const oldestAction = Array.from(this.activeActions)[0];
      this.cancelAction(oldestAction, 'Exceeded max concurrent actions');
    }

    // Emit action created event
    this.emit('actionCreated', action);
    
    logger.debug('Agent action tracked', {
      actionId,
      toolName,
      description,
      args: JSON.stringify(args, null, 2)
    });

    return actionId;
  }

  /**
   * Update action status with real-time feedback
   */
  updateActionStatus(
    actionId: string,
    status: AgentActionStatus,
    options: {
      progress?: number;
      message?: string;
      result?: any;
      error?: string;
    } = {}
  ): void {
    const action = this.actions.get(actionId);
    if (!action) {
      logger.warn('Attempted to update non-existent action', { actionId });
      return;
    }

    const timestamp = Date.now();
    const previousStatus = action.status;
    
    // Update action
    action.status = status;
    action.duration = timestamp - action.timestamp;
    
    if (options.result !== undefined) {
      action.result = options.result;
    }
    
    if (options.error) {
      action.error = options.error;
    }

    // Handle status transitions
    if (status === 'executing' && previousStatus === 'pending') {
      // Action started execution
      this.emit('actionStarted', action);
    } else if (['completed', 'failed', 'cancelled'].includes(status)) {
      // Action finished
      this.activeActions.delete(actionId);
      this.completedActions.push(actionId);
      
      // Maintain history size
      if (this.completedActions.length > this.maxHistorySize) {
        const removedActionId = this.completedActions.shift();
        if (removedActionId) {
          this.actions.delete(removedActionId);
        }
      }
      
      this.emit('actionCompleted', action);
    }

    // Create status update
    const update: ActionStatusUpdate = {
      actionId,
      status,
      timestamp,
      duration: action.duration,
      progress: options.progress,
      message: options.message,
      error: options.error,
      result: options.result
    };

    // Emit real-time status update
    this.emit('statusUpdate', update);
    
    logger.debug('Agent action status updated', {
      actionId,
      status,
      previousStatus,
      duration: action.duration,
      message: options.message
    });
  }

  /**
   * Cancel an active action
   */
  cancelAction(actionId: string, reason?: string): void {
    const action = this.actions.get(actionId);
    if (!action || !this.activeActions.has(actionId)) {
      return;
    }

    this.updateActionStatus(actionId, 'cancelled', {
      error: reason || 'Action cancelled',
      message: 'Action was cancelled'
    });
  }

  /**
   * Get action by ID
   */
  getAction(actionId: string): AgentAction | undefined {
    return this.actions.get(actionId);
  }

  /**
   * Get all active actions
   */
  getActiveActions(): AgentAction[] {
    return Array.from(this.activeActions)
      .map(id => this.actions.get(id))
      .filter((action): action is AgentAction => action !== undefined);
  }

  /**
   * Get recent completed actions
   */
  getRecentActions(limit = 10): AgentAction[] {
    return this.completedActions
      .slice(-limit)
      .map(id => this.actions.get(id))
      .filter((action): action is AgentAction => action !== undefined)
      .reverse();
  }

  /**
   * Get all actions for a specific canvas
   */
  getCanvasActions(canvasId: string): AgentAction[] {
    return Array.from(this.actions.values())
      .filter(action => action.metadata?.canvasId === canvasId);
  }

  /**
   * Generate canvas preview data for visual overlays
   */
  generateCanvasPreview(action: AgentAction): CanvasPreview | null {
    if (!this.config.showPreviews) {
      return null;
    }

    const canvasStore = useCanvasStore.getState();
    const activeCanvas = canvasStore.getActiveCanvas();
    
    if (!activeCanvas) {
      return null;
    }

    switch (action.type) {
      case 'createWidget':
        return this.generateWidgetPreview(action, activeCanvas.widgets);
      
      case 'connectWidgets':
        return this.generateConnectionPreview(action, activeCanvas.widgets);
      
      case 'updateWidget':
        return this.generateHighlightPreview(action, activeCanvas.widgets);
      
      default:
        return null;
    }
  }

  /**
   * Update visual feedback configuration
   */
  updateConfig(updates: Partial<VisualFeedbackConfig>): void {
    this.config = { ...this.config, ...updates };
    this.emit('configUpdated', this.config);
    
    logger.info('Agent feedback config updated', { config: this.config });
  }

  /**
   * Get current configuration
   */
  getConfig(): VisualFeedbackConfig {
    return { ...this.config };
  }

  /**
   * Clear all actions (useful for cleanup or reset)
   */
  clearActions(): void {
    this.actions.clear();
    this.activeActions.clear();
    this.completedActions = [];
    
    this.emit('actionsCleared');
    logger.info('All agent actions cleared');
  }

  /**
   * Get statistics about action execution
   */
  getStatistics() {
    const allActions = Array.from(this.actions.values());
    const completedCount = allActions.filter(a => a.status === 'completed').length;
    const failedCount = allActions.filter(a => a.status === 'failed').length;
    const avgDuration = allActions
      .filter(a => a.duration)
      .reduce((sum, a) => sum + (a.duration || 0), 0) / completedCount || 0;

    return {
      totalActions: allActions.length,
      activeActions: this.activeActions.size,
      completedActions: completedCount,
      failedActions: failedCount,
      successRate: completedCount / (completedCount + failedCount) || 0,
      averageDuration: Math.round(avgDuration),
      actionTypes: this.getActionTypeDistribution(allActions)
    };
  }

  /**
   * Cleanup completed actions and start timer
   */
  private startCleanupTimer(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }

    this.cleanupTimer = window.setInterval(() => {
      this.cleanupOldActions();
    }, 30000); // Clean up every 30 seconds
  }

  /**
   * Clean up old completed actions
   */
  private cleanupOldActions(): void {
    const cutoffTime = Date.now() - (5 * 60 * 1000); // 5 minutes ago
    const actionsToRemove: string[] = [];

    for (const [actionId, action] of this.actions) {
      if (['completed', 'failed', 'cancelled'].includes(action.status) && 
          action.timestamp < cutoffTime) {
        actionsToRemove.push(actionId);
      }
    }

    actionsToRemove.forEach(actionId => {
      this.actions.delete(actionId);
      const index = this.completedActions.indexOf(actionId);
      if (index > -1) {
        this.completedActions.splice(index, 1);
      }
    });

    if (actionsToRemove.length > 0) {
      logger.debug('Cleaned up old actions', { count: actionsToRemove.length });
    }
  }

  /**
   * Map tool name to action type
   */
  private mapToolNameToActionType(toolName: string): AgentActionType {
    const mapping: Record<string, AgentActionType> = {
      createWidget: 'createWidget',
      connectWidgets: 'connectWidgets',
      executeDataFlow: 'executeDataFlow',
      findWidgetByTypeAndTitle: 'findWidget',
      getWidgetData: 'getWidgetData',
      updateWidget: 'updateWidget',
      removeWidget: 'deleteWidget'
    };

    return mapping[toolName] || 'createWidget';
  }

  /**
   * Generate human-readable action description
   */
  private generateActionDescription(toolName: string, args: Record<string, any>): string {
    switch (toolName) {
      case 'createWidget':
        return `Creating ${args.type} widget${args.title ? ` "${args.title}"` : ''}`;
      
      case 'connectWidgets':
        return `Connecting widgets`;
      
      case 'executeDataFlow':
        return 'Executing data flow pipeline';
      
      case 'findWidgetByTypeAndTitle':
        return `Finding ${args.type} widget${args.title ? ` "${args.title}"` : ''}`;
      
      case 'getWidgetData':
        return 'Reading widget data';
      
      default:
        return `Executing ${toolName}`;
    }
  }

  /**
   * Generate widget creation preview
   */
  private generateWidgetPreview(action: AgentAction, widgets: Widget[]): CanvasPreview {
    // Calculate position for new widget
    const position = action.args.position || this.calculateNextWidgetPosition(widgets);
    
    return {
      id: nanoid(),
      type: 'widget',
      position,
      size: { width: 400, height: 300 }, // Default size
      style: {
        opacity: 0.6,
        color: this.config.highlightColor,
        animation: 'pulse',
        duration: this.config.animationDuration
      }
    };
  }

  /**
   * Generate connection preview
   */
  private generateConnectionPreview(action: AgentAction, widgets: Widget[]): CanvasPreview {
    const sourceWidget = widgets.find(w => w.id === action.args.sourceWidgetId);
    const targetWidget = widgets.find(w => w.id === action.args.targetWidgetId);
    
    if (!sourceWidget || !targetWidget) {
      return {
        id: nanoid(),
        type: 'connection',
        style: {
          opacity: 0.8,
          color: '#2196F3',
          animation: 'fade',
          duration: this.config.animationDuration
        }
      };
    }

    return {
      id: nanoid(),
      type: 'connection',
      sourceWidgetId: sourceWidget.id,
      targetWidgetId: targetWidget.id,
      style: {
        opacity: 0.8,
        color: '#2196F3',
        animation: 'slide',
        duration: 500
      }
    };
  }

  /**
   * Generate highlight preview for widget updates
   */
  private generateHighlightPreview(action: AgentAction, widgets: Widget[]): CanvasPreview {
    const targetWidget = widgets.find(w => w.id === action.targetWidgetId);
    
    return {
      id: nanoid(),
      type: 'highlight',
      targetWidgetId: action.targetWidgetId,
      position: targetWidget?.position,
      size: targetWidget?.size,
      style: {
        opacity: 0.4,
        color: '#FF9800',
        animation: 'glow',
        duration: 1000
      }
    };
  }

  /**
   * Calculate position for next widget
   */
  private calculateNextWidgetPosition(widgets: Widget[]): Position {
    if (widgets.length === 0) {
      return { x: 100, y: 100 };
    }

    // Find the rightmost widget and place new widget to its right
    const rightmostWidget = widgets.reduce((rightmost, widget) => {
      return widget.position.x > rightmost.position.x ? widget : rightmost;
    }, widgets[0]);

    return {
      x: rightmostWidget.position.x + rightmostWidget.size.width + 100,
      y: rightmostWidget.position.y
    };
  }

  /**
   * Get distribution of action types for statistics
   */
  private getActionTypeDistribution(actions: AgentAction[]): Record<AgentActionType, number> {
    const distribution: Record<AgentActionType, number> = {
      createWidget: 0,
      connectWidgets: 0,
      updateWidget: 0,
      deleteWidget: 0,
      executeDataFlow: 0,
      findWidget: 0,
      getWidgetData: 0
    };

    actions.forEach(action => {
      distribution[action.type]++;
    });

    return distribution;
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }
    
    this.removeAllListeners();
    this.clearActions();
    
    logger.info('AgentFeedbackService destroyed');
  }
}

// Export singleton instance
export const agentFeedbackService = new AgentFeedbackService();

// Type guards for action status
export const isActionPending = (action: AgentAction): boolean => action.status === 'pending';
export const isActionExecuting = (action: AgentAction): boolean => action.status === 'executing';
export const isActionCompleted = (action: AgentAction): boolean => action.status === 'completed';
export const isActionFailed = (action: AgentAction): boolean => action.status === 'failed';
export const isActionActive = (action: AgentAction): boolean => 
  ['pending', 'executing'].includes(action.status);